import { useLazyQuery } from '@apollo/client'
import {
  Button,
  Collapse,
  Hidden,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import FilterListIcon from '@material-ui/icons/FilterList'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'

import {
  buildFeatureSet,
  getCredentials,
  hasRequiredFeatures
} from '../../cache/auth'
import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import {
  BOOKINGLIST_QUERY,
  BOOKING_DOWNLOAD_EXCEL_QUERY
} from '../../graphql/bookingList'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import bookRoleManagement from '../../staticRoleManagement/bookRoleManagement'
import {
  Booking,
  BookingExcelFile,
  BookingListInput,
  CRSStatus,
  PMSStatus,
  URlFiltersData
} from '../../types/booking'
import BookingListFilters from './BookingListFilter'
import { URL_FILTERS_DEFAULT_VALUE } from './BookingListFilter/filtersListdefaultValue'
import { BookingsListTable } from './BookingsListTable'
import { BookingsListTableMobile } from './BookingsListTableMobile/BookingsListTableMobile'
import {
  decodeBookingExportPayload,
  exportFileWithinLimit
} from './exportFilePolicy'
import useStyles from './styles/index.styles'

const BookingList = () => {
  const classes = useStyles()
  const [perPage] = useState(8)
  const theme = useTheme()

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const filterParams = new URLSearchParams(location.search)
  const filterFields = filterParams.get('filter')
  const decodedFilterFields = filterFields && decodeURIComponent(filterFields)
  const newFilterFields: URlFiltersData =
    decodedFilterFields && JSON.parse(decodedFilterFields)

  const [urlFiltersData, setUrlFiltersData] = useState<URlFiltersData>(
    newFilterFields !== null ? newFilterFields : URL_FILTERS_DEFAULT_VALUE
  )
  const [filterButtonSelected, setFilterButtonSelected] = useState<boolean>(
    newFilterFields !== null
  )
  const [bookingListFiltersValues, setBookingListFiltersValues] =
    useState<BookingListInput>({
      tenantsCode: urlFiltersData.tenantsCode.map((i) => i.value),
      propertyCode: urlFiltersData.propertyCode.map((i) => i.value),
      roomtypeId: urlFiltersData.roomtypeId.map((i) => i.value),
      channelsId: urlFiltersData.channelsId.map((i) => i.value),
      pmsStatus: urlFiltersData.pmsStatus.map((i) => i.value as PMSStatus),
      crsStatus: urlFiltersData.crsStatus.map((i) => i.value as CRSStatus),
      reserveStartDate: urlFiltersData.reserveStartDate,
      reserveEndDate: urlFiltersData.reserveEndDate,
      startDate: urlFiltersData.startDate,
      endDate: urlFiltersData.endDate,
      departureStartDate: urlFiltersData.departureStartDate,
      departureEndDate: urlFiltersData.departureEndDate,
      roomstayInclusionDate: urlFiltersData.roomstayInclusionDate,
      roomCRSNumber: urlFiltersData.roomCRSNumber,
      pmsNumber: urlFiltersData.pmsNumber,
      sourceReservationNumber: urlFiltersData.sourceReservationNumber,
      guestName: urlFiltersData.guestName || '',
      masterCRSNumber: urlFiltersData.masterCRSNumber || '',
      page: 1,
      perPage: 8,
      currency: undefined,
      rateplanId: urlFiltersData.rateplanId.map((i) => i.value),
      systemAllocations: urlFiltersData.systemAllocations,
      providersCode: urlFiltersData.providersCode.map((i) => i.value)
    })

  useEffect(() => {
    setBookingListFiltersValues({
      ...bookingListFiltersValues,
      tenantsCode: urlFiltersData.tenantsCode.map((i) => i.value),
      propertyCode: urlFiltersData.propertyCode.map((i) => i.value),
      roomtypeId: urlFiltersData.roomtypeId.map((i) => i.value),
      rateplanId: urlFiltersData.rateplanId.map((i) => i.value),
      channelsId: urlFiltersData.channelsId.map((i) => i.value),
      pmsStatus: urlFiltersData.pmsStatus.map((i) => i.value as PMSStatus),
      crsStatus: urlFiltersData.crsStatus.map((i) => i.value as CRSStatus),
      reserveStartDate: urlFiltersData.reserveStartDate,
      reserveEndDate: urlFiltersData.reserveEndDate,
      startDate: urlFiltersData.startDate,
      departureStartDate: urlFiltersData.departureStartDate,
      departureEndDate: urlFiltersData.departureEndDate,
      endDate: urlFiltersData.endDate,
      roomstayInclusionDate: urlFiltersData.roomstayInclusionDate,
      roomCRSNumber: urlFiltersData.roomCRSNumber,
      pmsNumber: urlFiltersData.pmsNumber,
      sourceReservationNumber: urlFiltersData.sourceReservationNumber,
      guestName: urlFiltersData.guestName || '',
      masterCRSNumber: urlFiltersData.masterCRSNumber || '',
      currency: urlFiltersData.currency,
      systemAllocations: urlFiltersData.systemAllocations,
      providersCode: urlFiltersData.providersCode.map((i) => i.value)
    })
  }, [urlFiltersData])

  const [page, setPage] = useState(1)
  const [
    getBookingsList,
    { data, loading: loadingForGetBookingList, refetch }
  ] = useLazyQuery<{
    bookingList: Booking
  }>(BOOKINGLIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const handleGetBookingsList = () => {
    getBookingsList({
      variables: {
        input: {
          ...bookingListFiltersValues,
          page,
          perPage,
          isBasedOnRoomStays: false
        }
      }
    })
  }

  useEffect(() => {
    handleGetBookingsList()
  }, [page, perPage])

  const hasAccessToViewBookingDetails = bookRoleManagement
    .find((item) => item.route === '/booking/list/:reserveNumber/view')
    ?.rolesListWithReadAccess?.includes(getRoleFromStorage())

  const hasAccessToEditBookingDetails = bookRoleManagement
    .find((item) => item.route === '/booking/list/:reserveNumber/edit')
    ?.rolesListWithReadAccess?.includes(getRoleFromStorage())
  const exportSection = bookRoleManagement.find(
    (item) => item.route === '/booking/list'
  )?.sections?.VIEW_BOOKING_LIST_EXCEL
  const canExportBookings =
    !!exportSection &&
    hasRequiredFeatures(
      exportSection.requiredFeatures,
      buildFeatureSet(getCredentials())
    )

  const [getExcelData, { data: excelData, loading: loadingForExcelData }] =
    useLazyQuery<BookingExcelFile>(BOOKING_DOWNLOAD_EXCEL_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onError: (err) => {
        toast.error(err.message)
      }
    })
  function saveAs(blob: any, fileName: any) {
    var url = window.URL.createObjectURL(blob)

    var anchorElem = document.createElement('a')
    anchorElem.style.display = 'none'
    anchorElem.href = url
    anchorElem.download = fileName

    document.body.appendChild(anchorElem)
    anchorElem.click()

    document.body.removeChild(anchorElem)

    setTimeout(function () {
      window.URL.revokeObjectURL(url)
    }, 1000)
  }

  function generateExcelFile(
    exceldata: Record<string, unknown>[],
    fileName: string,
    maxFileBytes: number
  ) {
    const worksheet = XLSX.utils.json_to_sheet(exceldata)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet 1')
    const excelFile = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    })
    const blob = new Blob([excelFile], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    if (!exportFileWithinLimit(blob.size, maxFileBytes)) {
      toast.error(
        getTranslate('The export exceeds the configured file-size limit.')
      )
      return
    }
    saveAs(blob, fileName)
  }

  useEffect(() => {
    if (excelData?.bookingListExcel?.excelData) {
      try {
        const payload = decodeBookingExportPayload(
          excelData.bookingListExcel.excelData
        )
        generateExcelFile(payload.data, 'Booking.xlsx', payload.maxFileBytes)
      } catch {
        toast.error(getTranslate('Unable to generate the export file.'))
      }
    }
  }, [excelData?.bookingListExcel?.excelData])

  return (
    <>
      <PageHeader title={getTranslate('Bookings List')} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleGetBookingsList()
        }}>
        <div className={classes.mianDiv}>
          <div className={classes.header}>
            <SectionTitle title={getTranslate('Booking List')} />
            <div className={classes.buttonContainer}>
              {canExportBookings && (
                <div className={classes.buttonContainer}>
                  <Button
                    endIcon={<OpenInNewIcon />}
                    variant="contained"
                    disabled={loadingForExcelData || loadingForGetBookingList}
                    onClick={() => {
                      getExcelData({
                        variables: {
                          input: {
                            ...bookingListFiltersValues,
                            page: 0,
                            perPage: 0,
                            isBasedOnRoomStays: true
                          }
                        }
                      })
                    }}
                    className={classes.exportButton}>
                    {isSmallScreen
                      ? getTranslate('Room Stays')
                      : getTranslate('Export Room Stays')}
                  </Button>
                  <Button
                    endIcon={<OpenInNewIcon />}
                    variant="contained"
                    disabled={loadingForExcelData || loadingForGetBookingList}
                    onClick={() => {
                      getExcelData({
                        variables: {
                          input: {
                            ...bookingListFiltersValues,
                            page: 0,
                            perPage: 0,
                            isBasedOnRoomStays: false
                          }
                        }
                      })
                    }}
                    className={classes.exportButton}>
                    {isSmallScreen
                      ? getTranslate('Reservations')
                      : getTranslate('Export Reservations')}
                  </Button>
                </div>
              )}
              <Button
                size={isSmallScreen ? 'large' : 'medium'}
                startIcon={<FilterListIcon />}
                classes={{
                  startIcon: isSmallScreen ? classes.startIcon : undefined
                }}
                variant="contained"
                color="primary"
                onClick={() => setFilterButtonSelected(!filterButtonSelected)}>
                {isSmallScreen ? '' : getTranslate('Filter')}
              </Button>
            </div>
          </div>
          <Collapse in={filterButtonSelected}>
            <BookingListFilters
              bookingListFiltersValues={bookingListFiltersValues}
              setBookingListFiltersValues={setBookingListFiltersValues}
              onFindClick={handleGetBookingsList}
              setPage={setPage}
              setUrlFiltersData={setUrlFiltersData}
              urlFiltersData={urlFiltersData}
              perPage={perPage}
              loadingForGetBookingList={loadingForGetBookingList}
            />
          </Collapse>
          <Hidden smDown>
            <BookingsListTable
              hasAccessToViewBookingDetails={hasAccessToViewBookingDetails}
              hasAccessToEditBookingDetails={hasAccessToEditBookingDetails}
              data={data?.bookingList}
              refetch={refetch}
              loadingForGetBookingList={loadingForGetBookingList}
              page={page}
              setPage={setPage}
            />
          </Hidden>
        </div>
        <Hidden mdUp>
          <BookingsListTableMobile
            hasAccessToViewBookingDetails={hasAccessToViewBookingDetails}
            hasAccessToEditBookingDetails={hasAccessToEditBookingDetails}
            data={data?.bookingList}
            loadingForGetBookingList={loadingForGetBookingList}
          />
        </Hidden>
      </form>
    </>
  )
}

export default BookingList
